import React, { useCallback, useState, useEffect } from 'react'
import { FixedSizeList as List } from 'react-window'
import { useSelector } from 'react-redux'
import { Box } from '@chakra-ui/core'
import { RootState } from 'constants/interfaces'
import { ContextItem } from './ContextItem'
import { isRtlSelector } from 'redux/selectors/general'
import { NoResults } from 'components/CommonComponents/GroupsTree/NoResults'

interface IProps {
  onOpen: VoidFunction
  tid: string
  recurrenceTs: string | null
  isThereOverviewButtons: boolean
  contexts: {
    contextId: string
    status: number
    taskContextId: string
    name: string
    userDefaultGroup: string
  }[]
}

/** Space occupied by the responsive overview header above the virtualized list. */
const getViewportListHeight = (hasOverviewButtons: boolean) => {
  const width = window.innerWidth
  const reservedHeight =
    width < 768
      ? hasOverviewButtons
        ? 500
        : 445
      : width < 1280
      ? hasOverviewButtons
        ? 465
        : 405
      : width < 1536
      ? hasOverviewButtons
        ? 420
        : 305
      : hasOverviewButtons
      ? 405
      : 305

  return Math.max(0, window.innerHeight - reservedHeight)
}

export const ContextsList: React.FC<IProps> = ({ onOpen, contexts, tid, recurrenceTs, isThereOverviewButtons }) => {
  const { deepDiveInsights, insights: allInsights } = useSelector((state: RootState) => state.tasks_v2)

  const subtasks = allInsights[tid]?.sub_tasks || {}
  const subtasksCount = Object.keys(subtasks).length

  const isRtl = useSelector(isRtlSelector)

  const { insights } = deepDiveInsights

  const [height, setHeight] = useState(() => getViewportListHeight(isThereOverviewButtons))

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const item = contexts[index]

      if (!item) {
        return null
      }

      const { contextId, status, name, taskContextId, userDefaultGroup } = item
      const typeCounts = insights[contextId] ? insights[contextId].typeCounts : null

      return (
        <Box
          style={{
            ...style,
            paddingRight: isRtl ? 0 : 15,
            paddingLeft: isRtl ? 15 : 0,
            paddingTop: 5,
          }}
        >
          <ContextItem
            key={contextId}
            contextId={contextId}
            userDefaultGroup={userDefaultGroup}
            tid={tid}
            taskContextId={taskContextId}
            statusValue={status}
            typeCounts={typeCounts}
            subtasksCount={subtasksCount}
            onOpen={onOpen}
            recurrenceTs={recurrenceTs ?? null}
            name={name}
          />
        </Box>
      )
    },
    [contexts, insights, isRtl, tid, subtasksCount, onOpen, recurrenceTs]
  )

  useEffect(() => {
    const updateHeight = () => setHeight(getViewportListHeight(isThereOverviewButtons))

    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [isThereOverviewButtons])

  return (
    <>
      {contexts.length === 0 ? (
        <NoResults />
      ) : (
        <List
          height={height}
          itemCount={contexts.length}
          itemSize={105}
          width="calc(100% + 15px)"
          style={{
            direction: isRtl ? 'rtl' : 'ltr',
            marginBottom: '-20px',
            paddingBottom: '20px',
          }}
        >
          {Row}
        </List>
      )}
    </>
  )
}
